import React, { useState } from 'react';
import { X, Users, Check, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { copyToClipboard } from '../utils/clipboard';

const PartnerInviteModal = ({ isOpen, onClose, onInviteSent }) => {
    const [name, setName] = useState('');
    const [relation, setRelation] = useState('');
    const [sending, setSending] = useState(false);
    const [inviteUrl, setInviteUrl] = useState(null);

    const handleSendInvite = async () => {
        if (!name) {
            toast.error('Lütfen isim girin');
            return;
        }

        try {
            setSending(true);
            const authToken = localStorage.getItem('authToken');
            const response = await axios.post('/api/profile/partner/invite',
                { name, relation },
                { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
            );

            setInviteUrl(response.data.inviteUrl);
            toast.success('Davet linki oluşturuldu!');

            if (onInviteSent) {
                onInviteSent(response.data);
            }

            // --- AUTOMATIC WHATSAPP SHARE FOR INVITE ---
            const inviteMsg = encodeURIComponent(
                `👋 Selam şefim!

Seni "Bana Bir Tarif Ver" uygulamasında *Yardımcı Şef* olman için davet ediyorum. Birlikte ortak alışveriş listeleri oluşturup anlık takip edebiliriz! 🛒👨‍🍳

Daveti kabul etmek için bu linke tıkla:
${response.data.inviteUrl}`
            );
            window.open(`https://wa.me/?text=${inviteMsg}`, '_blank');
            // -------------------------------------------
        } catch (error) {
            console.error('Error sending invite:', error);
            const errorMsg = error.response?.data?.error || 'Davet gönderilemedi';

            if (errorMsg === 'already_has_partner') {
                toast.error('Zaten bir partneriniz var');
            } else {
                toast.error('Davet gönderilemedi');
            }
        } finally {
            setSending(false);
        }
    };

    const handleCopyLink = async () => {
        if (inviteUrl) {
            const success = await copyToClipboard(inviteUrl);
            if (success) {
                toast.success('Link kopyalandı!');
            } else {
                toast.error('Kopyalanamadı, lütfen manuel kopyalayın.');
            }
        }
    };

    const handleSkip = () => {
        localStorage.setItem('partnerInviteSkipped', 'true');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                <Users size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">Yardımcı Şef Ekleyelim mi?</h2>
                                <p className="text-sm text-blue-100">Birlikte Alışverişi Kolaylaştırın</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {!inviteUrl ? (
                    /* Invitation Form */
                    <div className="p-6">
                        <div className="mb-6">
                            <p className="text-gray-700 dark:text-gray-300 mb-4">
                                Şefim, bu kişiyle sık yardımlaşıyorsunuz! Onu <strong>'Yardımcı Şef'</strong> (Partner) olarak kaydedelim mi?
                            </p>

                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2">
                                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">Birlikte Neler Yapabilirsiniz?</p>
                                <div className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                                    <div className="flex items-center gap-2">
                                        <Check size={16} className="text-green-500" />
                                        <span>Otomatik liste paylaşımı</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Check size={16} className="text-green-500" />
                                        <span>Anlık senkronizasyon</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Check size={16} className="text-green-500" />
                                        <span>Ortak tasarruf raporları</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Check size={16} className="text-green-500" />
                                        <span>Bebek güvenliği bildirimleri</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        İsim
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Örn: Ahmet"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Yakınlık Derecesi
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Örn: Eşim"
                                        value={relation}
                                        onChange={(e) => setRelation(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={handleSkip}
                                className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition font-medium"
                            >
                                Şimdi Değil
                            </button>
                            <button
                                onClick={handleSendInvite}
                                disabled={sending}
                                className={`flex-1 px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${sending || !name
                                    ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                    }`}
                            >
                                {sending ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Gönderiliyor...
                                    </>
                                ) : (
                                    <>
                                        <Send size={18} />
                                        Davet Gönder
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    /* Invite Link Display */
                    <div className="p-6">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="text-green-500" size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                                Davet Linki Oluşturuldu!
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                Bu linki partnerinizle paylaşın
                            </p>
                        </div>

                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-4">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Davet Linki:</p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={inviteUrl}
                                    readOnly
                                    className="flex-1 px-3 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                                />
                                <button
                                    onClick={handleCopyLink}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                                >
                                    Kopyala
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                ⏱️ Link 7 gün geçerlidir
                            </p>
                        </div>

                        <button
                            onClick={onClose}
                            className="w-full px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition font-medium"
                        >
                            Kapat
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PartnerInviteModal;
